import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import dayjs from 'dayjs';

import { getPlanById } from '../services/plans.service';
import {
  getPlanNameOptions,
  getWitnessCompanyOptions,
} from '../components/materials/autocomplete-options';
import { defaultPlanValue } from '../stores/create-plan-table.store';

import { convertToTableData, validateTableData } from './use-excel-table';
import {
  handleGetCreateUpdateOrDeletedPlanRecords,
  syncDataBackToDetailHansontable,
} from './use-confirm';

import type {
  PlanTableData,
  PlanTableRow,
  RowsWithErrors,
} from '../components/common/handsontable.types';
import type { PropertyPlansApiResponse } from '../types/plan.types';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { FunctionName, PermissionAction, snackbarType } from '@/lib/enums';
import { SearchOption } from '@/types/search';
import { MESSAGES } from '@/lib/constants';
import { useSnackbar } from '@/hooks/use-snackbar';

const EDIT_VIEW_BLANK_ROWS_COUNT = 5;

export const usePlanDetail = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasPast = searchParams.get('hasPast') === 'true';
  const hasArchive = searchParams.get('hasArchive') === 'true';
  const [filters, setFilters] = useState({
    isShowPast: hasPast,
    isShowArchive: hasArchive,
  });
  const [countPlans, setCountPlans] = useState({
    past: 0,
    present: 0,
  });

  const [planDetail, setPlanDetail] = useState<PropertyPlansApiResponse | null>(null);
  const { setIsLoading } = useLoadingProgress();
  const [tableData, setTableData] = useState<PlanTableData>([]);
  const [tableUpdateData, setTableUpdateData] = useState<PlanTableData>([]);
  const [tableCreateNewData, setTableCreateNewData] = useState<PlanTableData>([]);
  const [tableDeletedData, setTableDeletedData] = useState<PlanTableData>([]);
  const [originalTableData, setOriginalTableData] = useState<PlanTableData>([]);
  const [plantOptions, setPlantOptions] = useState<SearchOption[]>([]);
  const [witnessCompanyOptions, setWitnessCompanyOptions] = useState<SearchOption[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [rowsWithErrors, setRowsWithErrors] = useState<RowsWithErrors>([]);
  // Validation errors for confirm view tables
  const [updateSectionErrors, setUpdateSectionErrors] = useState<
    { rowIndex: number; field: { index: number; message: string }[] }[]
  >([]);
  const [createSectionErrors, setCreateSectionErrors] = useState<
    { rowIndex: number; field: { index: number; message: string }[] }[]
  >([]);
  const [viewMode, setViewMode] = useState<'edit' | 'confirm'>('edit');
  const hasLoadedRef = useRef(false); // Prevent double API calls (React StrictMode)
  const isSyncingFromConfirmRef = useRef(false); // Track if we're syncing from confirm view
  const hasInitializedTableDataRef = useRef(false); // Track if tableData has been initialized
  // Track which row indices are currently visible (based on original data and filters)
  // This ensures records remain visible even if user changes their date to a past date
  const visibleRowIndicesRef = useRef<Set<number>>(new Set());
  // Track which row indices have been edited by user (date changed)
  // This ensures edited records remain visible even if their new date doesn't pass filter
  const editedRowIndicesRef = useRef<Set<number>>(new Set());
  // Track original dates of rows that were visible on initial load (based on initial filters)
  // This helps distinguish between "old records" (past dates from initial load) and "edited records" (dates changed by user)
  const initialVisibleRowDatesRef = useRef<Map<number, string>>(new Map());
  // IMPORTANT: Track which rows are "old records" (past dates) vs "current/future records"
  // This flag is set ONCE when data is initialized from API
  // Filter will only affect "old records", not "current/future records"
  // This ensures editing current/future records is not affected by filter changes
  const oldRecordIndicesRef = useRef<Set<number>>(new Set());

  const { setSnackbarMessage } = useSnackbar();

  // Filter plan deliveries based on filters
  const filteredPlanDeliveries = useMemo(() => {
    if (!planDetail?.planDeliveries) return [];
    return planDetail.planDeliveries.filter((el) => {
      const isFuture = filters.isShowPast
        ? true
        : dayjs(el.placementDate).endOf('day').isAfter(dayjs());
      const isArchive = filters.isShowArchive ? true : !el.isArchive;
      return isFuture && isArchive;
    });
  }, [planDetail?.planDeliveries, filters]);

  // Reset initialization flag when planId changes
  useEffect(() => {
    hasInitializedTableDataRef.current = false;
    hasLoadedRef.current = false;
    oldRecordIndicesRef.current.clear(); // Clear old record indices when planId changes
    editedRowIndicesRef.current.clear(); // Clear edited row indices when planId changes
    initialVisibleRowDatesRef.current.clear(); // Clear initial visible row dates when planId changes
  }, [planId]);

  useEffect(() => {
    if (hasLoadedRef.current) {
      return;
    }

    const loadOptions = async () => {
      hasLoadedRef.current = true;
      setIsLoading(true);
      if (!planId) return;
      try {
        const [plants, witnesses, plan] = await Promise.all([
          getPlanNameOptions(FunctionName.SCHEDULE_MANAGEMENT, PermissionAction.UPDATE),
          getWitnessCompanyOptions(FunctionName.SCHEDULE_MANAGEMENT, PermissionAction.UPDATE),
          getPlanById(planId, true, true),
        ]);
        setPlantOptions(plants);
        setWitnessCompanyOptions(witnesses);
        setPlanDetail(plan as PropertyPlansApiResponse);
        setOptionsLoaded(true);
      } catch (error) {
        console.error('[useRegisterPlan] Error loading options:', error);
        setOptionsLoaded(true);
        navigate('/404');
        setIsLoading(false);
      }
    };

    void loadOptions();
  }, [setIsLoading, planId, navigate, hasPast, hasArchive]);

  // Update countPlans when filters change (separate from tableData initialization)
  useEffect(() => {
    if (planDetail?.planDeliveries) {
      const countPlansPast = planDetail.planDeliveries.filter((el) => {
        const isPast = dayjs(el.placementDate).endOf('day').isBefore(dayjs());
        const isArchive = filters.isShowArchive ? true : !el.isArchive;
        return isPast && isArchive;
      }).length;
      const countPlansPresent = planDetail.planDeliveries.filter((el) => {
        const isFuture = dayjs(el.placementDate).endOf('day').isAfter(dayjs());
        const isArchive = filters.isShowArchive ? true : !el.isArchive;
        return isFuture && isArchive;
      }).length;
      setCountPlans({
        past: countPlansPast,
        present: countPlansPresent,
      });
    }
  }, [planDetail?.planDeliveries, filters]);

  // Initialize tableData only once when data is first loaded
  // Use ALL planDeliveries (not filtered) so we preserve all data when filters change
  // Filtering will be applied in filteredTableData for display only
  // IMPORTANT: Classify records as "old" (past dates) or "current/future" (future dates) at initialization
  // This classification is permanent and not affected by filter changes
  useEffect(() => {
    if (optionsLoaded && planDetail && !hasInitializedTableDataRef.current) {
      // Don't overwrite tableData if we're currently syncing from confirm view
      if (isSyncingFromConfirmRef.current) {
        isSyncingFromConfirmRef.current = false;
        return;
      }

      // Use ALL planDeliveries, not filteredPlanDeliveries
      // This ensures all data is preserved when filters change
      const data = convertToTableData(
        planDetail.planDeliveries || [],
        plantOptions,
        witnessCompanyOptions,
        true,
        planDetail?.canUpdate
      );

      const blankRows = convertToTableData(
        Array.from({ length: EDIT_VIEW_BLANK_ROWS_COUNT }, () => ({ ...defaultPlanValue })),
        plantOptions,
        witnessCompanyOptions,
        true,
        planDetail?.canUpdate
      );
      const dataWithBlankRows = [...data, ...blankRows];

      // IMPORTANT: Classify records as "old" (past dates) or "current/future" (future dates)
      // This classification happens ONCE at initialization and is permanent
      // Filter will only affect "old records", not "current/future records"
      const oldRecordIndices = new Set<number>();
      const today = dayjs();

      dataWithBlankRows.forEach((row, index) => {
        // Row structure: [..., placementDate (index 2), ..., id (index 23), isDeleted (index 24), isArchive (index 25)]
        if (row.length < 26) {
          // New rows (incomplete data) are considered "current/future"
          return;
        }

        const placementDateStr = row[2]; // placementDate at index 2 (format: YY/MM/DD)
        if (placementDateStr && String(placementDateStr).trim()) {
          try {
            // Parse YY/MM/DD format
            const parts = String(placementDateStr).trim().split('/');
            if (parts.length === 3) {
              const year = Number.parseInt(parts[0], 10);
              const month = Number.parseInt(parts[1], 10);
              const day = Number.parseInt(parts[2], 10);
              const fullYear = year < 100 ? 2000 + year : year;
              const dateStr = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

              // Check if date is in the past
              const isPastDate = dayjs(dateStr).endOf('day').isBefore(today);
              if (isPastDate) {
                // This is an "old record" (past date)
                oldRecordIndices.add(index);
              }
              // Otherwise, it's a "current/future record" (future date)
            }
          } catch (error) {
            // If parsing fails, treat as current/future by default
            console.error('Error parsing placementDate:', error);
          }
        }
        // If no date, treat as current/future by default
      });

      // Store old record indices
      oldRecordIndicesRef.current = oldRecordIndices;

      // IMPORTANT: Create deep copy for originalTableData to prevent mutation
      // If we use the same reference, any changes to tableData will also mutate originalTableData
      const originalDataCopy = JSON.parse(JSON.stringify(dataWithBlankRows));
      setTableData(dataWithBlankRows);
      setOriginalTableData(originalDataCopy);
      hasInitializedTableDataRef.current = true;
      setIsLoading(false);
    }
  }, [optionsLoaded, plantOptions, witnessCompanyOptions, planDetail, setIsLoading]);

  const handleSubmit = () => {
    // IMPORTANT: Only validate records that are currently visible (filtered)
    // If user hides old records, they should not be validated or checked for changes
    const { rowsWithErrors } = validateTableData(
      filteredTableData,
      plantOptions,
      witnessCompanyOptions
    );
    if (rowsWithErrors.length > 0) {
      // Map filtered indices back to full tableData indices for error display
      const fullIndexErrors = rowsWithErrors.map((error) => {
        const fullIndex = filteredToFullIndexMap.get(error.rowIndex);
        if (fullIndex !== undefined) {
          return {
            rowIndex: fullIndex,
            field: error.field,
          };
        }
        return error; // Fallback (shouldn't happen)
      });
      setRowsWithErrors(fullIndexErrors);
      return;
    } else {
      // IMPORTANT: Only check changes for records that are currently visible (filtered)
      // Analyze and categorize records into update, create, and delete
      // Use filteredTableData and filteredOriginalTableData to only process visible records
      const { tableUpdateData, tableCreateNewData, tableDeletedData } =
        handleGetCreateUpdateOrDeletedPlanRecords(filteredTableData, filteredOriginalTableData);

      setTableUpdateData(tableUpdateData);
      setTableCreateNewData(tableCreateNewData);
      setTableDeletedData(tableDeletedData);

      // Validate confirm tables and set initial errors
      // Validate update section: only validate updated rows (odd indices)
      const updateRowsToValidate = tableUpdateData.filter((_, index) => index % 2 === 1);
      const { rowsWithErrors: updateErrorsRaw } = validateTableData(
        updateRowsToValidate,
        plantOptions,
        witnessCompanyOptions
      );
      // Map errors back to tableUpdateData indices (odd indices)
      const updateErrors = updateErrorsRaw.map((error) => ({
        ...error,
        rowIndex: error.rowIndex * 2 + 1, // Map from updateRowsToValidate index to tableUpdateData index
      }));
      setUpdateSectionErrors(updateErrors);

      // Validate create section
      const { rowsWithErrors: createErrors } = validateTableData(
        tableCreateNewData,
        plantOptions,
        witnessCompanyOptions
      );
      setCreateSectionErrors(createErrors);

      // Switch to confirm view mode
      setViewMode('confirm');

      // Open snackbar to confirm changes (persist until user closes manually)
      setSnackbarMessage(MESSAGES.CONFIRM_CHANGES, { type: snackbarType.SUCCESS, persist: true });
    }
  };

  const handleBackToEdit = () => {
    // Set flag to prevent useEffect from overwriting synced data
    isSyncingFromConfirmRef.current = true;

    // Check if there are any changes in confirm view tables
    // If no changes, skip sync to avoid unnecessary updates
    const hasUpdateChanges = tableUpdateData.length > 0;
    const hasCreateChanges = tableCreateNewData.length > 0;

    if (!hasUpdateChanges && !hasCreateChanges) {
      // No changes in confirm view, just switch back to edit view
      setRowsWithErrors([]);
      setUpdateSectionErrors([]);
      setCreateSectionErrors([]);
      setViewMode('edit');
      isSyncingFromConfirmRef.current = false;
      return;
    }

    // Sync data from confirm view back to tableData
    const { syncedTableData, mergedErrors, hasDataChanged } = syncDataBackToDetailHansontable(
      tableData,
      tableUpdateData,
      tableCreateNewData,
      updateSectionErrors,
      createSectionErrors,
      rowsWithErrors
    );

    // Always update tableData if we have changes from confirm view
    // This ensures the latest data from confirm view is always reflected in edit view
    if (hasDataChanged) {
      setTableData(syncedTableData);
    }

    // Reset flag after a short delay to allow state update to complete
    setTimeout(() => {
      isSyncingFromConfirmRef.current = false;
    }, 100);

    // Set errors and clear confirm view errors
    setRowsWithErrors(mergedErrors);
    setUpdateSectionErrors([]);
    setCreateSectionErrors([]);
    setViewMode('edit');
  };

  const handleAddRow = () => {
    const newRow = convertToTableData(
      [{ ...defaultPlanValue }],
      plantOptions,
      witnessCompanyOptions,
      true
    )[0];
    setTableData([...tableData, newRow]);
  };

  // Handle data changes in update section (confirm view)
  // IMPORTANT: Only update tableUpdateData state, don't sync to tableData immediately
  // Sync will happen when user clicks back (handleBackToEdit)
  // This prevents incorrect data from being synced and ensures proper sync order
  // IMPORTANT: Preserve metadata (id, isDeleted, isArchive) when Handsontable returns only 23 columns
  const handleUpdateSectionDataChange = useCallback(
    (data: PlanTableData) => {
      const ID_COLUMN_INDEX = 23;

      // Ensure all rows have 26 columns by preserving metadata from current tableUpdateData
      const preservedData = data.map((row, rowIndex) => {
        if (row.length >= 26) {
          // Already has full 26 columns, use as is
          return [...row];
        } else if (row.length === 23) {
          // Handsontable returned only 23 columns, preserve metadata from current tableUpdateData
          const currentRow = tableUpdateData[rowIndex];
          if (currentRow && currentRow.length >= 26) {
            return [
              ...row, // First 23 columns from Handsontable (with user edits)
              currentRow[ID_COLUMN_INDEX] || '', // id (preserve from current)
              currentRow[24] || '0', // isDeleted (preserve from current)
              currentRow[25] || '0', // isArchive (preserve from current)
            ];
          } else {
            // If no current row, create with empty metadata (shouldn't happen normally)
            return [
              ...row,
              '', // id
              '0', // isDeleted
              '0', // isArchive
            ];
          }
        }
        return row;
      });

      setTableUpdateData(preservedData);
      // Don't sync to tableData here - wait until handleBackToEdit
    },
    [tableUpdateData]
  );

  // Handle data changes in create section (confirm view)
  // IMPORTANT: Only update tableCreateNewData state, don't sync to tableData immediately
  // Sync will happen when user clicks back (handleBackToEdit)
  // This prevents incorrect data from being synced and ensures proper sync order
  const handleCreateSectionDataChange = useCallback((data: PlanTableData) => {
    setTableCreateNewData(data);
    // Don't sync to tableData here - wait until handleBackToEdit
  }, []);

  // Reset function to restore original table data
  const resetAllForms = useCallback(() => {
    setTableData(originalTableData);
    setRowsWithErrors([]);
    setUpdateSectionErrors([]);
    setCreateSectionErrors([]);
  }, [originalTableData]);

  // Helper function to parse placement date from YY/MM/DD format
  const parsePlacementDate = useCallback((placementDateStr: unknown): string | null => {
    if (!placementDateStr || !String(placementDateStr).trim()) {
      return null;
    }

    try {
      const parts = String(placementDateStr).trim().split('/');
      if (parts.length !== 3) {
        return null;
      }

      const year = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10);
      const day = Number.parseInt(parts[2], 10);

      // Convert YY to YYYY (assume 20XX for years < 100)
      const fullYear = year < 100 ? 2000 + year : year;
      return `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // If parsing fails, return null
      return null;
    }
  }, []);

  // Helper function to check if date is future based on filter
  const checkIfDateIsFuture = useCallback(
    (placementDateStr: unknown): boolean => {
      const dateStr = parsePlacementDate(placementDateStr);
      if (!dateStr) {
        // Default to true if no date
        return true;
      }

      if (filters.isShowPast) {
        // Show all (past and future)
        return true;
      }

      // Only show future dates
      return dayjs(dateStr).endOf('day').isAfter(dayjs());
    },
    [filters.isShowPast, parsePlacementDate]
  );

  // Helper function to check if archive matches filter
  const checkArchiveMatch = useCallback(
    (isArchive: boolean): boolean => {
      return filters.isShowArchive ? true : !isArchive;
    },
    [filters.isShowArchive]
  );

  // Helper function to filter table data based on current filters
  // This is used for both tableData and originalTableData
  const filterTableDataByFilters = useCallback(
    (data: PlanTableData) => {
      if (!data || data.length === 0) {
        return [];
      }

      const filtered: PlanTableData = [];

      data.forEach((row) => {
        // Row structure: [..., placementDate (index 2), ..., id (index 23), isDeleted (index 24), isArchive (index 25)]
        if (row.length < 26) {
          // Keep rows with incomplete data (new rows)
          filtered.push(row);
          return;
        }

        const placementDateStr = row[2]; // placementDate at index 2 (format: YY/MM/DD)
        const isArchive = row[25] === '1' || row[25] === 1; // isArchive at index 25

        const isFuture = checkIfDateIsFuture(placementDateStr);
        const isArchiveMatch = checkArchiveMatch(isArchive);

        if (isFuture && isArchiveMatch) {
          filtered.push(row);
        }
      });

      return filtered;
    },
    [checkIfDateIsFuture, checkArchiveMatch]
  );

  // Helper function to check if a row is a new row (incomplete data)
  const isNewRow = useCallback((row: PlanTableRow): boolean => {
    return row.length < 26;
  }, []);

  // Helper function to extract isArchive from row
  const getIsArchive = useCallback((row: PlanTableRow): boolean => {
    return row[25] === '1' || row[25] === 1;
  }, []);

  // Helper function to check if old record should be visible
  const shouldIncludeOldRecord = useCallback(
    (row: PlanTableRow): boolean => {
      const placementDateStr = row[2]; // placementDate at index 2 (format: YY/MM/DD)
      const isArchive = getIsArchive(row);

      const isFuture = checkIfDateIsFuture(placementDateStr);
      const isArchiveMatch = checkArchiveMatch(isArchive);

      return isFuture && isArchiveMatch;
    },
    [checkIfDateIsFuture, checkArchiveMatch, getIsArchive]
  );

  // Helper function to check if current/future record should be visible
  const shouldIncludeCurrentFutureRecord = useCallback(
    (row: PlanTableRow): boolean => {
      const isArchive = getIsArchive(row);
      return checkArchiveMatch(isArchive);
    },
    [checkArchiveMatch, getIsArchive]
  );

  // Track which row indices are currently visible based on originalTableData and filters
  // IMPORTANT: This is used for calendar to show dots
  // Filter only affects "old records", "current/future records" are always visible
  useEffect(() => {
    if (!originalTableData || originalTableData.length === 0) {
      visibleRowIndicesRef.current.clear();
      initialVisibleRowDatesRef.current.clear();
      return;
    }

    const visibleIndices = new Set<number>();

    originalTableData.forEach((row, fullIndex) => {
      // Row structure: [..., placementDate (index 2), ..., id (index 23), isDeleted (index 24), isArchive (index 25)]
      if (isNewRow(row)) {
        // Keep rows with incomplete data (new rows) - always visible
        visibleIndices.add(fullIndex);
        return;
      }

      // IMPORTANT: Check if this is an "old record" (past date from initial load)
      const isOldRecord = oldRecordIndicesRef.current.has(fullIndex);

      if (isOldRecord) {
        // This is an "old record" - filter applies to it
        if (shouldIncludeOldRecord(row)) {
          visibleIndices.add(fullIndex);
        }
        return;
      }

      // This is a "current/future record" - ALWAYS visible, regardless of filter
      if (shouldIncludeCurrentFutureRecord(row)) {
        visibleIndices.add(fullIndex);
      }
    });

    visibleRowIndicesRef.current = visibleIndices;
  }, [
    originalTableData,
    filters,
    isNewRow,
    shouldIncludeOldRecord,
    shouldIncludeCurrentFutureRecord,
  ]);

  // Helper function to add row to filtered data and update index map
  const addRowToFiltered = useCallback(
    (
      row: PlanTableRow,
      filtered: PlanTableData,
      indexMap: Map<number, number>,
      fullIndex: number
    ) => {
      filtered.push(row);
      indexMap.set(filtered.length - 1, fullIndex);
    },
    []
  );

  // Helper function to process old record in filtered table data
  const processOldRecord = useCallback(
    (
      row: PlanTableRow,
      fullIndex: number,
      filtered: PlanTableData,
      indexMap: Map<number, number>
    ): void => {
      if (shouldIncludeOldRecord(row)) {
        addRowToFiltered(row, filtered, indexMap, fullIndex);
      }
    },
    [shouldIncludeOldRecord, addRowToFiltered]
  );

  // Helper function to process current/future record in filtered table data
  const processCurrentFutureRecord = useCallback(
    (
      row: PlanTableRow,
      fullIndex: number,
      filtered: PlanTableData,
      indexMap: Map<number, number>
    ): void => {
      if (shouldIncludeCurrentFutureRecord(row)) {
        addRowToFiltered(row, filtered, indexMap, fullIndex);
      }
    },
    [shouldIncludeCurrentFutureRecord, addRowToFiltered]
  );

  // Filter tableData based on filters while preserving user changes
  // IMPORTANT: Filter ONLY affects "old records" (past dates from initial load)
  // "Current/future records" are ALWAYS visible, regardless of filter
  // Edited records (current/future records that were edited to past) are also always visible
  // Also create a map from filtered index to full tableData index
  const { filteredTableData, filteredToFullIndexMap } = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return { filteredTableData: tableData, filteredToFullIndexMap: new Map<number, number>() };
    }

    const filtered: PlanTableData = [];
    const indexMap = new Map<number, number>();

    tableData.forEach((row, fullIndex) => {
      // Row structure: [..., placementDate (index 2), ..., id (index 23), isDeleted (index 24), isArchive (index 25)]
      if (isNewRow(row)) {
        // Keep rows with incomplete data (new rows) - these are always "current/future"
        addRowToFiltered(row, filtered, indexMap, fullIndex);
        return;
      }

      // IMPORTANT: Check if this is an "old record" (past date from initial load)
      const isOldRecord = oldRecordIndicesRef.current.has(fullIndex);

      if (isOldRecord) {
        // This is an "old record" - filter applies to it
        processOldRecord(row, fullIndex, filtered, indexMap);
      } else {
        // This is a "current/future record" - ALWAYS show it, regardless of filter
        // This includes:
        // - Records that were future/current at initial load
        // - Records that were edited (even if edited to past date)
        // - New rows added by user
        processCurrentFutureRecord(row, fullIndex, filtered, indexMap);
      }
    });

    return { filteredTableData: filtered, filteredToFullIndexMap: indexMap };
  }, [
    tableData,
    filters,
    isNewRow,
    addRowToFiltered,
    processOldRecord,
    processCurrentFutureRecord,
  ]);

  // Filter originalTableData based on current filters
  // IMPORTANT: Only validate and check changes for records that are currently visible
  // If user hides old records and submits, those records should be ignored
  const filteredOriginalTableData = useMemo(() => {
    return filterTableDataByFilters(originalTableData);
  }, [originalTableData, filterTableDataByFilters]);

  // Define row manipulation callbacks after filteredToFullIndexMap is available
  const handleAddRowAbove = useCallback(
    (index: number) => {
      // index is from filtered table, need to map to full table index
      const fullIndex = filteredToFullIndexMap.get(index);
      if (fullIndex === undefined) {
        // If index not found in map, use the index directly (shouldn't happen)
        return;
      }

      // Create new empty row (default values)
      const newRow = convertToTableData(
        [{ ...defaultPlanValue }],
        plantOptions,
        witnessCompanyOptions,
        true
      )[0];
      // Insert new row at correct position in current data
      const updatedTableData = [...tableData];
      updatedTableData.splice(fullIndex, 0, newRow);

      // Update tableData state
      setTableData(updatedTableData);
    },
    [plantOptions, witnessCompanyOptions, tableData, filteredToFullIndexMap]
  );

  const handleAddRowBelow = useCallback(
    (index: number) => {
      // index is from filtered table, need to map to full table index
      const fullIndex = filteredToFullIndexMap.get(index);
      if (fullIndex === undefined) {
        // If index not found in map, use the index directly (shouldn't happen)
        return;
      }

      // Create new empty row (default values)
      const newRow = convertToTableData(
        [{ ...defaultPlanValue }],
        plantOptions,
        witnessCompanyOptions,
        true
      )[0];

      // Insert new row at correct position in current data
      const updatedTableData = [...tableData];
      updatedTableData.splice(fullIndex + 1, 0, newRow);

      // Update tableData state
      setTableData(updatedTableData);
    },
    [plantOptions, witnessCompanyOptions, tableData, filteredToFullIndexMap]
  );

  const handleDeleteRows = useCallback(
    (indices: number[]) => {
      if (!Array.isArray(indices) || indices.length === 0) {
        return;
      }

      const fullIndices = Array.from(
        new Set(
          indices
            .map((index) => filteredToFullIndexMap.get(index))
            .filter((index): index is number => typeof index === 'number' && index >= 0)
        )
      ).sort((a, b) => b - a);

      if (fullIndices.length === 0) {
        return;
      }

      const updatedTableData = [...tableData];
      fullIndices.forEach((fullIndex) => {
        if (fullIndex >= 0 && fullIndex < updatedTableData.length) {
          updatedTableData.splice(fullIndex, 1);
        }
      });

      if (updatedTableData.length === 0) {
        const newRow = convertToTableData(
          [{ ...defaultPlanValue }],
          plantOptions,
          witnessCompanyOptions,
          true
        )[0];
        updatedTableData.push(newRow);
      }

      setTableData(updatedTableData);
    },
    [tableData, filteredToFullIndexMap, plantOptions, witnessCompanyOptions]
  );

  // Map rowsWithErrors from full tableData index to filteredTableData index
  // This ensures validation errors show on the correct rows when filters are applied
  const filteredRowsWithErrors = useMemo(() => {
    if (!rowsWithErrors || rowsWithErrors.length === 0) {
      return [];
    }

    // Create reverse map: fullIndex -> filteredIndex
    const fullToFilteredIndexMap = new Map<number, number>();
    filteredToFullIndexMap.forEach((fullIndex, filteredIndex) => {
      fullToFilteredIndexMap.set(fullIndex, filteredIndex);
    });

    // Map errors from full index to filtered index
    return rowsWithErrors
      .map((error) => {
        const filteredIndex = fullToFilteredIndexMap.get(error.rowIndex);
        if (filteredIndex !== undefined) {
          return {
            rowIndex: filteredIndex,
            field: error.field,
          };
        }
        return null; // Row is filtered out, skip this error
      })
      .filter(
        (error): error is { rowIndex: number; field: { index: number; message: string }[] } =>
          error !== null
      );
  }, [rowsWithErrors, filteredToFullIndexMap]);

  // Wrapper for setRowsWithErrors to map filtered index back to full index
  // When validation errors are set from filtered table, we need to convert back to full index
  const setRowsWithErrorsFiltered = useCallback(
    (
      errors:
        | { rowIndex: number; field: { index: number; message: string }[] }[]
        | ((
            prev: { rowIndex: number; field: { index: number; message: string }[] }[]
          ) => { rowIndex: number; field: { index: number; message: string }[] }[])
    ) => {
      if (typeof errors === 'function') {
        // If it's a function, we need to convert the current filtered errors to full errors first
        setRowsWithErrors((prevFullErrors) => {
          // Convert filtered errors to full errors for the function
          const fullToFilteredIndexMap = new Map<number, number>();
          filteredToFullIndexMap.forEach((fullIndex, filteredIndex) => {
            fullToFilteredIndexMap.set(fullIndex, filteredIndex);
          });
          const filteredErrors = prevFullErrors
            .map((error) => {
              const filteredIndex = fullToFilteredIndexMap.get(error.rowIndex);
              if (filteredIndex !== undefined) {
                return {
                  rowIndex: filteredIndex,
                  field: error.field,
                };
              }
              return null;
            })
            .filter(
              (error): error is { rowIndex: number; field: { index: number; message: string }[] } =>
                error !== null
            );

          // Call the function with filtered errors
          const newFilteredErrors = errors(filteredErrors);

          // Convert back to full errors
          return newFilteredErrors.map((error) => {
            const fullIndex = filteredToFullIndexMap.get(error.rowIndex);
            if (fullIndex !== undefined) {
              return {
                rowIndex: fullIndex,
                field: error.field,
              };
            }
            return error; // Fallback (shouldn't happen)
          });
        });
      } else {
        // Convert filtered indices to full indices
        const fullErrors = errors.map((error) => {
          const fullIndex = filteredToFullIndexMap.get(error.rowIndex);
          if (fullIndex !== undefined) {
            return {
              rowIndex: fullIndex,
              field: error.field,
            };
          }
          return error; // Fallback (shouldn't happen)
        });
        setRowsWithErrors(fullErrors);
      }
    },
    [setRowsWithErrors, filteredToFullIndexMap, rowsWithErrors]
  );

  // Update handleTableDataChange to work with filtered data
  // When Handsontable sends filtered data, we need to map it back to full tableData
  const handleTableDataChangeFiltered = useCallback(
    (data: PlanTableData) => {
      // data is filtered, but we need to update full tableData
      // Use the map to find corresponding rows in full tableData
      const fullTableDataCopy = [...tableData];

      // Track which rows have been edited (date changed) to keep them visible
      const editedIndices = new Set<number>();

      // Update full tableData with changes from filtered data using the index map
      data.forEach((newRow, filteredIndex) => {
        const fullIndex = filteredToFullIndexMap.get(filteredIndex);
        if (fullIndex !== undefined && fullIndex < fullTableDataCopy.length) {
          const existingRow = fullTableDataCopy[fullIndex];
          if (existingRow) {
            // Check if placementDate (index 2) has changed
            const PLACEMENT_DATE_COLUMN_INDEX = 2;
            const originalDate = existingRow[PLACEMENT_DATE_COLUMN_INDEX];
            const newDate = newRow[PLACEMENT_DATE_COLUMN_INDEX];

            // If date has changed, mark this row as edited
            if (originalDate !== newDate) {
              editedIndices.add(fullIndex);
            }

            if (existingRow.length >= 26) {
              // Preserve metadata and update with new data
              fullTableDataCopy[fullIndex] = [
                ...newRow, // First 23 columns from Handsontable
                existingRow[23] || '', // id
                existingRow[24] || '0', // isDeleted
                existingRow[25] || '0', // isArchive
              ];
            } else {
              // Row with incomplete data, just update with new data and add metadata
              fullTableDataCopy[fullIndex] = [
                ...newRow,
                '', // id
                '0', // isDeleted
                '0', // isArchive
              ];
            }
          }
        }
      });

      // Update editedRowIndicesRef with newly edited rows
      // Keep existing edited rows and add new ones
      editedIndices.forEach((index) => {
        editedRowIndicesRef.current.add(index);
      });

      setTableData(fullTableDataCopy);
    },
    [tableData, filteredToFullIndexMap]
  );

  return {
    planDetail,
    filteredPlanDeliveries,
    tableData: filteredTableData, // Return filtered tableData for display
    fullTableData: tableData, // Return full tableData (all records) for calendar to show dots immediately
    visibleRowIndices: visibleRowIndicesRef.current, // Return visible row indices for calendar to track edited dates
    editedRowIndices: editedRowIndicesRef.current, // Return edited row indices for calendar to show dots for edited dates
    oldRecordIndices: oldRecordIndicesRef.current, // Return old record indices for calendar to distinguish old vs current/future records
    originalTableData,
    filteredOriginalTableData, // Return filtered originalTableData for change detection
    tableUpdateData,
    tableCreateNewData,
    tableDeletedData,
    plantOptions,
    witnessCompanyOptions,
    filters,
    setFilters,
    countPlans,
    rowsWithErrors: filteredRowsWithErrors, // Return filtered rowsWithErrors with correct indices
    setRowsWithErrors: setRowsWithErrorsFiltered, // Use filtered version that maps indices correctly
    updateSectionErrors,
    setUpdateSectionErrors,
    createSectionErrors,
    setCreateSectionErrors,
    viewMode,
    handleSubmit,
    handleBackToEdit,
    handleAddRow,
    handleAddRowAbove,
    handleAddRowBelow,
    handleDeleteRows,
    handleTableDataChange: handleTableDataChangeFiltered, // Use filtered version
    handleUpdateSectionDataChange,
    handleCreateSectionDataChange,
    resetAllForms,
  };
};
